/**
 * Returns a callback that guarantees the canvas agent floating chat is
 * open: it re-activates the most-recent existing main session, or mints
 * a fresh `CANVAS_AGENT` session if none exist. Raises the window when a
 * session is already active but minimized to the FAB.
 *
 * Used by canvas window-event handlers (`useCanvasWindowEvents`) that
 * need to surface the chat without each call site re-implementing the
 * session-resolution logic. Callers that want the composer prefilled pass
 * the text along, since only this hook knows which session that will be.
 */

import { useCreateFlowSession } from "@/api/flow_sessions";
import { pushComposerDraft } from "@/composer/composerDraft";
import Sentry from "@/integrations/Sentry";
import { FLOW_SESSION_MODES, type FlowSession } from "@cohesive/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import toast from "react-hot-toast";
import { useCanvasFloatingChat } from "../context/CanvasFloatingChatContext";
import { useCanvasRoute } from "../context/CanvasRouteContext";

export function useEnsureCanvasAgentChatOpen(): (
  composerDraft?: string,
) => Promise<void> {
  const { canvasId } = useCanvasRoute();
  const queryClient = useQueryClient();
  const {
    openSession: openCanvasFloatingSession,
    currentSessionId,
    open,
    expand,
  } = useCanvasFloatingChat();
  const createFlowSession = useCreateFlowSession();

  return useCallback(
    async (composerDraft?: string) => {
      if (!canvasId) return;

      // Seed the composer of the session the chat actually lands on: keyed by
      // session, the draft can only ever be claimed by that one composer.
      const seedComposer = (sessionId: string) => {
        if (composerDraft) {
          pushComposerDraft(queryClient, sessionId, composerDraft);
        }
      };

      if (currentSessionId) {
        seedComposer(currentSessionId);
        // A session is already active but the window may be minimized to the
        // FAB — callers ask for a chat the user can see, so raise it.
        if (!open) expand();
        return;
      }

      // Prefer reactivating the most-recently-updated existing main
      // session (action-item sessions are excluded — they're scoped to a
      // specific node, not the canvas-wide agent chat).
      const sessions =
        queryClient.getQueryData<FlowSession[]>([
          "canvas-flow-sessions",
          canvasId,
        ]) ?? [];
      const mainSessions = sessions.filter((s) => !s.action_item_id);
      const sorted = [...mainSessions].sort(
        (a, b) =>
          new Date(b.updated_at ?? 0).getTime() -
          new Date(a.updated_at ?? 0).getTime(),
      );
      if (sorted.length > 0) {
        seedComposer(sorted[0]!.id);
        openCanvasFloatingSession(sorted[0]!.id);
        return;
      }

      // No existing session — mint a fresh canvas-agent one.
      try {
        const newSession = await createFlowSession.mutateAsync({
          mode: FLOW_SESSION_MODES.CANVAS_CHAT,
          class: "canvas",
          canvas_id: canvasId,
        });
        await queryClient.invalidateQueries({
          queryKey: ["canvas-flow-sessions", canvasId],
        });
        seedComposer(newSession.id);
        openCanvasFloatingSession(newSession.id);
      } catch (err) {
        Sentry.captureException(err);
        toast.error("Failed to open chat");
      }
    },
    [
      canvasId,
      currentSessionId,
      open,
      expand,
      queryClient,
      openCanvasFloatingSession,
      createFlowSession,
    ],
  );
}
